"use client";

import { CoverageMeter } from "./CoverageMeter";
import { type CoverageCounts, coverageTotal } from "./coverage";
import { plural } from "./FunctionTile";
import type { MapLinkComponent } from "./link";

export interface FunctionCardProps {
  name: string;
  description?: string | null;
  /** The function's page; the whole card links there. */
  href: string;
  coverage: CoverageCounts;
  applicationCount: number;
  linkComponent?: MapLinkComponent;
}

/**
 * A business function as a card in an area's grid: its name and description,
 * its size, and one coverage bar that breaks down on hover. The whole card
 * opens the function.
 */
export function FunctionCard({
  name,
  description,
  href,
  coverage,
  applicationCount,
  linkComponent: Link = "a",
}: FunctionCardProps) {
  return (
    <article className="relative flex h-full flex-col rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-white) p-4 transition-colors focus-within:border-(--border-strong) hover:border-(--border-strong)">
      <h3 className="text-[14px]! font-semibold">
        {/* Stretched link: the whole card opens the function. */}
        <Link
          href={href}
          className="no-underline! text-inherit! after:absolute after:inset-0 after:rounded-(--radius-container) focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-(--fg-primary)"
        >
          {name}
        </Link>
      </h3>
      {description && (
        <p className="mt-0.5! text-[12px] leading-relaxed text-(--fg-muted)!">
          {description}
        </p>
      )}
      <p className="mt-1.5! text-[11px] tabular-nums text-(--fg-muted)!">
        {plural(coverageTotal(coverage), "step")} ·{" "}
        {plural(applicationCount, "application")}
      </p>
      <div className="mt-auto pt-2.5">
        <CoverageMeter coverage={coverage} />
      </div>
    </article>
  );
}
